/**
 * Legal Document Page Component
 * Renders legal documents with proper jurisdiction switching
 */
import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLegalContent, DOCUMENT_TYPES } from '@/hooks/useLegalContent';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, AlertCircle, ArrowLeft } from 'lucide-react';

export default function LegalPage() {
  const { documentType = 'terms' } = useParams();
  const { t } = useTranslation();
  
  // Map URL param to document type
  const docType = documentType === 'privacy' ? DOCUMENT_TYPES.PRIVACY : 
                  documentType === 'cookies' ? DOCUMENT_TYPES.COOKIES : 
                  DOCUMENT_TYPES.TERMS;
  
  const { 
    document, 
    loading, 
    error, 
    jurisdiction, 
    version, 
    effectiveDate,
    sections,
    companyName 
  } = useLegalContent(docType);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !document) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">{t('legal.notAvailable')}</h2>
            <p className="text-muted-foreground mb-4">
              {t('legal.documentNotFound')}
            </p>
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-primary hover:underline"
            >
              <ArrowLeft className="h-4 w-4" />
              {t('common.backToHome')}
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('common.back')}
          </Link>
          
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {sections.introduction?.title || t(`legal.${documentType}`)}
              </h1>
              <p className="text-muted-foreground mt-1">{companyName}</p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="uppercase">
                {jurisdiction}
              </Badge>
              <Badge variant="secondary">
                v{version}
              </Badge>
            </div>
          </div>
          
          <p className="text-sm text-muted-foreground mt-4">
            {t('legal.effectiveDate')}: {effectiveDate}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Card>
          <CardContent className="p-6 md:p-8 space-y-8">
            {Object.entries(sections).map(([key, section]) => (
              <section key={key} className="scroll-mt-20" id={key}>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">
                  {section.title}
                </h2>
                <div className="prose prose-gray max-w-none">
                  {section.content.split('\n').map((paragraph, idx) => (
                    <p key={idx} className="text-gray-600 mb-2 whitespace-pre-line">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </section>
            ))}
          </CardContent>
        </Card>

        {/* Footer info */}
        <div className="mt-8 text-center text-sm text-muted-foreground">
          <p>{t('legal.lastUpdated')}: {effectiveDate}</p>
          <p className="mt-1">{t('legal.jurisdiction')}: {document.jurisdiction}</p>
        </div>
      </div>
    </div>
  );
}
